import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { downloadTicketPdf } from "../lib/pdf";
import { CheckCircle, Warning, CircleNotch, ShieldCheck, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BkashCallbackPage() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState("processing");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const pending = sessionStorage.getItem("bkash_pending");
    if (!pending) {
      setStatus("error");
      setError("No pending bKash payment found. Please try again.");
      return;
    }

    const { order_id, payment_id, event_id } = JSON.parse(pending);
    const urlPaymentId = searchParams.get("paymentID");
    const urlStatus = searchParams.get("status");

    if (!urlPaymentId && !payment_id) {
      setStatus("error");
      setError("Missing payment information from bKash.");
      return;
    }

    const executePayment = async () => {
      try {
        const { data } = await api.post("/bkash/execute", {
          payment_id: urlPaymentId || payment_id,
          order_id: order_id,
        });
        setStatus("success");
        setResult(data);
        sessionStorage.removeItem("bkash_pending");
        sessionStorage.removeItem("pending_checkout");
        toast.success("bKash payment successful! Your tickets are ready.");
      } catch (e) {
        setStatus("error");
        setError(e.response?.data?.detail || "Payment execution failed");
        sessionStorage.removeItem("bkash_pending");
      }
    };

    if (urlStatus === "cancel") {
      setStatus("cancelled");
      setError("Payment was cancelled.");
      sessionStorage.removeItem("bkash_pending");
    } else if (urlStatus === "failure") {
      setStatus("error");
      setError("bKash payment failed. Please try again.");
      sessionStorage.removeItem("bkash_pending");
    } else {
      executePayment();
    };
  }, [searchParams]);

  if (status === "processing") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <CircleNotch size={48} weight="bold" className="animate-spin text-[#E2136E] mx-auto" />
        <h1 className="font-display text-3xl mt-6 tracking-tighter">Processing your bKash payment…</h1>
        <p className="text-neutral-600 mt-3">Please wait while we confirm your payment.</p>
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <Warning size={48} weight="fill" className="text-amber-500 mx-auto" />
        <h1 className="font-display text-3xl mt-6 tracking-tighter">Payment cancelled</h1>
        <p className="text-neutral-600 mt-3">{error}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/events" className="btn-outline">Browse events</Link>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <Warning size={48} weight="fill" className="text-red-500 mx-auto" />
        <h1 className="font-display text-3xl mt-6 tracking-tighter">Payment failed</h1>
        <p className="text-neutral-600 mt-3">{error}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/events" className="btn-red">Try again</Link>
          <Link to="/dashboard" className="btn-outline">Go to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-20">
      <div className="flex items-center gap-3 text-[#10B981]">
        <CheckCircle size={36} weight="fill" />
        <span className="label-eyebrow">bKash payment confirmed</span>
      </div>
      <h1 className="font-display text-5xl mt-4 tracking-tighter">Enjoy the show.</h1>
      <p className="mt-4 text-neutral-600">Your bKash payment was successful. Your tickets are in your dashboard.</p>

      {result && (
        <>
          <div className="mt-10 ticket-stub p-6 my-6 rounded-2xl shadow-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="label-eyebrow">Order #{result.order.id.slice(0, 8)}</p>
                <h2 className="font-display text-2xl mt-2">{result.order.event_title}</h2>
                <p className="text-sm text-neutral-600">{new Date(result.order.event_date).toLocaleString()}</p>
                <p className="text-sm text-neutral-600">{result.order.venue}</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-widest text-neutral-500">Total</p>
                <p className="font-display text-2xl">৳{result.order.total.toLocaleString()}</p>
                {result.bkash_trx_id && (
                  <p className="text-xs text-[#E2136E] mt-1 font-mono">bKash TrxID: {result.bkash_trx_id}</p>
                )}
              </div>
            </div>
          </div>

          {result.tickets && result.tickets.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {result.tickets.map((t) => (
                <div key={t.id} className="border border-neutral-200 p-4 flex items-center gap-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <img src={t.qr_data_url} alt="QR" className="w-24 h-24 rounded-lg" />
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest font-bold">{t.ticket_type_name}</p>
                    <p className="text-xs font-mono text-[#4F46E5] mt-1">{t.serial_number}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{t.gate}</p>
                    <p className="font-display text-xl mt-2">৳{t.price.toLocaleString()}</p>
                    <button
                      onClick={async () => {
                        const ok = await downloadTicketPdf(t.id, t.serial_number);
                        if (ok) toast.success("PDF downloaded"); else toast.error("Download failed");
                      }}
                      className="mt-2 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:text-[#4F46E5]"
                    ><DownloadSimple size={14} weight="bold" /> Download PDF</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-[#F8F9FA] border border-neutral-200 text-xs text-neutral-600 flex items-start gap-3 rounded-xl">
            <ShieldCheck size={16} className="text-[#E2136E] mt-0.5" />
            <p>Payment verified via bKash. Each ticket has a unique serial number and QR code that organizers will scan at the gate.</p>
          </div>
        </>
      )}

      <div className="mt-10 flex gap-3">
        <Link to="/dashboard" className="btn-red">View my tickets</Link>
        <Link to="/events" className="btn-outline">Browse more</Link>
      </div>
    </div>
  );
}
